import React from "react";
import { formatInTimeZone } from "date-fns-tz";
import { timeToDate } from "utils/dateTimeUtils";
import { WorkingHoursSelectorEdit } from "./WorkingHoursSelectorEdit";
import { WorkingHoursSelectorView } from "./WorkingHoursSelectorView";

const defaultStartTime = "08:00 AM";
const defaultEndTime = "05:00 PM";

export type WorkingHoursRange = {
  start: string;
  end: string;
};

export type WorkingHoursSelectorSharedProps = {
  day: string;
  isActive: boolean;
  timeRanges: WorkingHoursRange[];
  clinicianTimeZone: string;
};

export type UpdateDayData = (
  day: string,
  isActive: boolean,
  timeRanges: WorkingHoursRange[]
) => void;

export type CopyToDays = (chosenDay: string, selectedDays: string[]) => void;

export type WorkingHoursSelectorProps = WorkingHoursSelectorSharedProps & {
  editMode: boolean;
  updateDayData: UpdateDayData;
  copyToDays: CopyToDays;
};

/**
 * Builds the fallback interval used when an active day has no saved intervals.
 */
const createDefaultNewRange = (clinicianTimeZone: string): WorkingHoursRange => ({
  start: formatInTimeZone(
    timeToDate(defaultStartTime, new Date(), clinicianTimeZone),
    clinicianTimeZone,
    "yyyy-MM-dd'T'HH:mm:ssXXX"
  ),
  end: formatInTimeZone(
    timeToDate(defaultEndTime, new Date(), clinicianTimeZone),
    clinicianTimeZone,
    "yyyy-MM-dd'T'HH:mm:ssXXX"
  ),
});

/**
 * Selects the edit or read-only working-hours UI without owning either mode's
 * implementation details.
 */
export const WorkingHoursSelector = ({
  day,
  isActive,
  timeRanges,
  updateDayData,
  copyToDays,
  editMode,
  clinicianTimeZone,
}: WorkingHoursSelectorProps) => {
  const defaultNewRange = createDefaultNewRange(clinicianTimeZone);
  const displayedTimeRanges = isActive && timeRanges.length === 0 ? [defaultNewRange] : timeRanges;
  const sharedProps = {
    day,
    isActive,
    timeRanges: displayedTimeRanges,
    clinicianTimeZone,
  };

  if (editMode) {
    return (
      <WorkingHoursSelectorEdit
        {...sharedProps}
        defaultNewRange={defaultNewRange}
        updateDayData={updateDayData}
        copyToDays={copyToDays}
      />
    );
  }

  return <WorkingHoursSelectorView {...sharedProps} />;
};
